import React, { useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Textarea } from "./ui/textarea";
import { Checkbox } from "./ui/checkbox";
import { Label } from "./ui/label";
import { useToast } from "../hooks/use-toast";
import { useAuth } from "../context/AuthContext";
import { fmtMDT } from "../lib/time";
import { cn } from "../lib/utils";
import {
  ExternalLink, Flame, Save, User, MessageSquare, Clock, Circle, AlertCircle, ShieldAlert,
} from "lucide-react";

const SEV_COLOR = {
  "E1 - Critical": { bg: "bg-rose-500/10", text: "text-rose-700 dark:text-rose-300", border: "border-rose-500/40" },
  "E2 - Major":    { bg: "bg-amber-500/10", text: "text-amber-700 dark:text-amber-300", border: "border-amber-500/40" },
  "E3 - Low":      { bg: "bg-sky-500/10", text: "text-sky-700 dark:text-sky-300", border: "border-sky-500/40" },
};
const DEFAULT_SEV = { bg: "bg-muted", text: "text-muted-foreground", border: "border-border" };
const sevStyle = (name) => SEV_COLOR[name] || DEFAULT_SEV;

const STATUS_STYLE = {
  triage:     "bg-slate-500/10 text-slate-700 dark:text-slate-300 border-slate-500/40",
  live:       "bg-orange-500/10 text-orange-700 dark:text-orange-300 border-orange-500/40",
  learning:   "bg-violet-500/10 text-violet-700 dark:text-violet-300 border-violet-500/40",
  closed:     "bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border-emerald-500/40",
  paused:     "bg-yellow-500/10 text-yellow-700 dark:text-yellow-300 border-yellow-500/40",
  canceled:   "bg-muted text-muted-foreground border-border",
  declined:   "bg-muted text-muted-foreground border-border",
};

const OPEN_CATEGORIES = new Set(["triage", "live", "learning"]);

export default function IncidentDetailModal({ incidentId, open, onClose, onEdited }) {
  const { toast } = useToast();
  const { user } = useAuth();
  const isAdmin = user?.role === "Super Admin" || (Array.isArray(user?.scopes) && user.scopes.includes("administrator"));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [newSeverityId, setNewSeverityId] = useState("");
  const [newStatusId, setNewStatusId] = useState("");
  const [notifyChannel, setNotifyChannel] = useState(false);

  const load = useCallback(async () => {
    if (!incidentId) return;
    setLoading(true);
    try {
      const { data: payload } = await api.get(`/incident-io/incidents/${incidentId}`);
      setData(payload);
      setNewSeverityId("");
      setNewStatusId("");
      setNotifyChannel(false);
    } catch (e) {
      toast({ title: "Couldn't load incident details", description: e?.response?.data?.detail || "", variant: "destructive" });
    } finally { setLoading(false); }
  }, [incidentId, toast]);

  useEffect(() => { if (open && incidentId) load(); }, [open, incidentId, load]);

  const isOpenIncident = data && OPEN_CATEGORIES.has(data.incident?.status_category);
  const canAction = isOpenIncident && isAdmin;
  const somethingChanged = !!(newSeverityId || newStatusId);

  const save = async () => {
    if (!somethingChanged) return;
    setSaving(true);
    try {
      await api.post(`/incident-io/incidents/${incidentId}/edit`, {
        severity_id: newSeverityId || undefined,
        incident_status_id: newStatusId || undefined,
        notify_incident_channel: notifyChannel,
      });
      toast({ title: "Incident updated", description: "Changes pushed to incident.io." });
      await load();
      onEdited?.();
    } catch (e) {
      toast({ title: "Update failed", description: e?.response?.data?.detail || "", variant: "destructive" });
    } finally { setSaving(false); }
  };

  const inc = data?.incident;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose?.(); }}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="incident-detail-modal">
        <DialogHeader>
          <div className="flex items-center gap-2 text-[11px] font-mono text-muted-foreground">
            {inc?.reference || "…"}
            {inc?.permalink && (
              <a href={inc.permalink} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-primary hover:underline text-[11px]">
                <ExternalLink className="w-3 h-3" /> Open in incident.io
              </a>
            )}
          </div>
          <DialogTitle className="pr-6 text-[18px]">
            {loading ? "Loading incident…" : (inc?.name || "Incident")}
          </DialogTitle>
          {inc && (
            <div className="flex items-center gap-2 pt-1">
              <SeverityPill name={inc.severity_name} />
              <StatusPill name={inc.status_name} category={inc.status_category} />
              {inc.incident_type && inc.incident_type !== "Undefined" && (
                <span className="text-[11px] text-muted-foreground">· {inc.incident_type}</span>
              )}
              {inc.mode && inc.mode !== "standard" && (
                <span className="text-[11px] text-muted-foreground">· {inc.mode}</span>
              )}
            </div>
          )}
          {inc?.summary && (
            <DialogDescription className="pt-2 text-[12.5px] leading-relaxed whitespace-pre-wrap">{inc.summary}</DialogDescription>
          )}
        </DialogHeader>

        {loading ? (
          <div className="space-y-3 py-4">
            {[...Array(3)].map((_, i) => <div key={i} className="h-14 rounded-lg bg-muted/40 animate-pulse" />)}
          </div>
        ) : !data ? (
          <div className="py-8 text-center text-[13px] text-muted-foreground">No data.</div>
        ) : (
          <div className="space-y-5 pt-2">

            {/* Roles */}
            {data.roles.length > 0 && (
              <Section title="Roles" icon={User}>
                <div className="grid grid-cols-2 gap-2">
                  {data.roles.map((r, i) => (
                    <div key={i} className="rounded-lg border border-border p-2.5">
                      <div className="text-[11px] uppercase tracking-wide text-muted-foreground">{r.role_name}</div>
                      <div className="text-[13px] font-medium mt-0.5">{r.assignee_name || <span className="italic text-muted-foreground">Unassigned</span>}</div>
                      {r.assignee_email && <div className="text-[11.5px] text-muted-foreground">{r.assignee_email}</div>}
                    </div>
                  ))}
                </div>
              </Section>
            )}

            {/* Actions — Super Admin only, and only when open */}
            {canAction && (
              <Section title="Take action" icon={ShieldAlert} accent="rose">
                <div className="rounded-lg border border-rose-500/30 bg-rose-500/5 p-3.5 space-y-3">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <Label className="text-[11.5px]">New severity</Label>
                      <Select value={newSeverityId} onValueChange={setNewSeverityId}>
                        <SelectTrigger data-testid="incident-new-severity"><SelectValue placeholder="Keep current" /></SelectTrigger>
                        <SelectContent>
                          {data.severities.map((s) => (
                            <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-[11.5px]">New status</Label>
                      <Select value={newStatusId} onValueChange={setNewStatusId}>
                        <SelectTrigger data-testid="incident-new-status"><SelectValue placeholder="Keep current" /></SelectTrigger>
                        <SelectContent>
                          {data.statuses
                            .filter((s) => !["merged", "declined", "canceled"].includes(s.category))
                            .map((s) => (
                              <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                            ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <label className="flex items-center gap-2 text-[12.5px] cursor-pointer">
                    <Checkbox checked={notifyChannel} onCheckedChange={(v) => setNotifyChannel(!!v)} data-testid="incident-notify-channel" />
                    Notify the incident Slack channel about this change
                  </label>
                  <div className="flex items-center justify-between pt-1">
                    <p className="text-[11.5px] text-muted-foreground">Changes push straight to incident.io.</p>
                    <Button size="sm" disabled={!somethingChanged || saving} onClick={save} className="gap-1.5" data-testid="incident-save-btn">
                      <Save className="w-3.5 h-3.5" /> {saving ? "Saving…" : "Save changes"}
                    </Button>
                  </div>
                </div>
              </Section>
            )}
            {isOpenIncident && !isAdmin && (
              <div className="rounded-lg border border-amber-500/40 bg-amber-500/5 p-2.5 text-[12px] text-amber-800 dark:text-amber-200 inline-flex items-start gap-2">
                <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                Only administrators can edit an open incident. Ask a Super Admin, or open it directly in incident.io.
              </div>
            )}

            {/* Timeline */}
            <Section title="Timeline" icon={Clock}>
              <ol className="relative border-l border-border ml-2 space-y-3">
                {data.timeline.map((t, i) => {
                  const hasValue = !!t.value;
                  return (
                    <li key={i} className="pl-4">
                      <span className={cn(
                        "absolute -left-1.5 mt-1.5 w-3 h-3 rounded-full border-2 border-background",
                        hasValue ? "bg-primary" : "bg-muted",
                      )} />
                      <div className={cn("flex items-center gap-2 text-[12.5px]", hasValue ? "" : "text-muted-foreground")}>
                        <span className="font-medium">{t.name}</span>
                        {hasValue ? (
                          <span className="text-muted-foreground">· {fmtMDT(t.value)}</span>
                        ) : (
                          <span className="italic text-[11.5px]">Not yet</span>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ol>
            </Section>

            {/* Updates history */}
            <Section title="Update history" icon={MessageSquare} count={data.updates.length}>
              {data.updates.length === 0 ? (
                <div className="text-[12.5px] text-muted-foreground italic">No updates recorded yet.</div>
              ) : (
                <div className="space-y-2">
                  {data.updates.map((u) => (
                    <div key={u.id} className="rounded-lg border border-border p-3">
                      <div className="flex items-center justify-between gap-2 flex-wrap">
                        <div className="text-[12px] font-medium">{u.updater || "System"}</div>
                        <div className="text-[11px] text-muted-foreground">{fmtMDT(u.created_at)}</div>
                      </div>
                      {u.message && <div className="text-[12.5px] mt-1 whitespace-pre-wrap">{u.message}</div>}
                      {(u.new_status_name || u.new_severity_name) && (
                        <div className="flex items-center gap-2 mt-2">
                          {u.new_severity_name && <SeverityPill name={u.new_severity_name} />}
                          {u.new_status_name && <StatusPill name={u.new_status_name} category={u.new_status_category} />}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </Section>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="incident-close-btn">Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Section({ title, icon: Icon, count, accent = "primary", children }) {
  const accentBg = accent === "rose" ? "bg-rose-500/10 border-rose-500/30" : "bg-primary/10 border-primary/20";
  const accentText = accent === "rose" ? "text-rose-500" : "text-primary";
  return (
    <section>
      <div className="flex items-center gap-2 mb-2">
        <div className={cn("w-6 h-6 rounded-md border flex items-center justify-center", accentBg)}>
          {Icon && <Icon className={cn("w-3.5 h-3.5", accentText)} />}
        </div>
        <h4 className="text-[13.5px] font-semibold">{title}</h4>
        {count != null && <span className="text-[11px] text-muted-foreground">{count}</span>}
      </div>
      {children}
    </section>
  );
}

function SeverityPill({ name }) {
  const s = sevStyle(name);
  return (
    <span className={cn("inline-flex items-center rounded-full border text-[10.5px] font-semibold px-2 py-0.5", s.bg, s.text, s.border)}>
      {name || "—"}
    </span>
  );
}

function StatusPill({ name, category }) {
  return (
    <span className={cn("inline-flex items-center rounded-full border text-[10.5px] font-semibold px-2 py-0.5 uppercase tracking-wide", STATUS_STYLE[category] || "bg-muted text-muted-foreground border-border")}>
      {name || category || "—"}
    </span>
  );
}
